import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE, apiFetch, showAppConfirm } from './api';
import { theme } from './theme';

const HOUR_WIDTH = 60;
const ROW_HEIGHT = 60;
const VEHICLE_LABEL_WIDTH = 170;
const MINUTES_PER_DAY = 24 * 60;
const TIMELINE_WIDTH = HOUR_WIDTH * 24;

const DIRECTION_COLORS = {
  AUSGEHEND: '#039eff',
  EINGEHEND_LEERGUT: '#ff9800',
  EINGEHEND_RETOURE: '#ff5555',
};

const DIRECTION_LABELS = {
  AUSGEHEND: 'Ausgehend',
  EINGEHEND_LEERGUT: 'Eingehend (Leergut)',
  EINGEHEND_RETOURE: 'Eingehend (Retoure)',
};

function getLocalDateInputValue(date = new Date()) {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().split('T')[0];
}

function parseDateInput(dateStr) {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function minutesToHHMM(min) {
  if (isNaN(min)) return '00:00';
  const totalMinutes = Math.round(min);
  if (totalMinutes === MINUTES_PER_DAY) return '24:00';
  const normalizedMinutes = ((totalMinutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const h = Math.floor(normalizedMinutes / 60);
  const m = normalizedMinutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function unixToDate(unix) {
  if (!unix) return '-';
  return new Date(unix * 1000).toLocaleDateString('de-DE');
}

function unixToTime(unix) {
  if (!unix) return '00:00';
  const date = new Date(unix * 1000);
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

function getSlotJobs(slot) {
  const customJobName = String(slot?.custom_job_name || '').trim();
  const rentmanJobs = Array.isArray(slot?.jobs) ? slot.jobs
    .filter(job => job.job_id)
    .map(job => job.job_name || `Job #${job.job_id}`) : [];
  return [
    ...(customJobName ? [customJobName] : []),
    ...rentmanJobs,
  ];
}

function getDayBounds(dateStr) {
  const date = parseDateInput(dateStr);
  if (!date) return null;
  date.setHours(0, 0, 0, 0);
  const dayStart = Math.floor(date.getTime() / 1000);
  return {
    dayStart,
    dayEnd: dayStart + MINUTES_PER_DAY * 60,
  };
}

function getVisibleSlotRange(slot, dateStr) {
  const bounds = getDayBounds(dateStr);
  if (!bounds) return null;
  if (!slot.start_time || !slot.end_time || slot.end_time <= bounds.dayStart || slot.start_time >= bounds.dayEnd) {
    return null;
  }

  const visibleStartUnix = Math.max(slot.start_time, bounds.dayStart);
  const visibleEndUnix = Math.min(slot.end_time, bounds.dayEnd);
  return {
    startUnix: slot.start_time,
    startMin: (visibleStartUnix - bounds.dayStart) / 60,
    endMin: (visibleEndUnix - bounds.dayStart) / 60,
  };
}

function layoutVehicleSlots(vehicleSlots, dateStr) {
  const lanes = [];
  const items = [...vehicleSlots]
    .map(slot => {
      const range = getVisibleSlotRange(slot, dateStr);
      if (!range) return null;
      const { startMin, endMin, startUnix } = range;
      let lane = lanes.findIndex(laneEnd => laneEnd <= startMin);

      if (lane === -1) {
        lane = lanes.length;
        lanes.push(endMin);
      } else {
        lanes[lane] = endMin;
      }

      return { slot, startMin, endMin, lane, startUnix };
    })
    .filter(Boolean)
    .sort((a, b) => a.startUnix - b.startUnix);

  return { items, laneCount: Math.max(1, lanes.length) };
}

const styles = {
  stack: { display: 'grid', gap: '20px', width: '100%', maxWidth: '100%', minWidth: 0 },
  card: { ...theme.glassCard, padding: '24px', color: 'var(--board-text-strong, #fff)', width: '100%', maxWidth: '100%', minWidth: 0 },
  tableWrap: { overflowX: 'auto', width: '100%', maxWidth: '100%', minWidth: 0 },
  table: theme.table,
  th: theme.th,
  td: theme.td,
  input: { ...theme.input, marginRight: '8px', marginBottom: '8px' },
  inlineInput: { ...theme.input, padding: '6px 8px', width: '100%' },
  select: { ...theme.select, marginRight: '8px', marginBottom: '8px', minWidth: '190px', colorScheme: 'dark' },
  inlineSelect: { ...theme.select, padding: '6px 8px', width: '100%', colorScheme: 'dark' },
  option: { backgroundColor: 'var(--board-surface, #161322)', color: 'var(--board-text-strong, #fff)' },
  button: { ...theme.button, ...theme.primaryButton },
  deleteBtn: { ...theme.button, ...theme.dangerButton, padding: '7px 12px', fontSize: '12px' },
  editBtn: { ...theme.button, ...theme.warningButton, padding: '7px 12px', fontSize: '12px', marginRight: '4px' },
  saveBtn: { ...theme.button, ...theme.successButton, padding: '7px 12px', fontSize: '12px', marginRight: '4px' },
  cancelBtn: { ...theme.button, ...theme.mutedButton, padding: '7px 12px', fontSize: '12px', marginRight: '4px' },
  badge: { padding: '5px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 800, backgroundColor: 'rgba(34,197,94,0.18)', border: '1px solid rgba(74,222,128,0.34)', color: 'var(--board-success-text, #dcfce7)' },
  inactiveBadge: { padding: '5px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 800, backgroundColor: 'rgba(148,163,184,0.16)', border: '1px solid rgba(148,163,184,0.34)', color: 'var(--board-text-soft, #e2e8f0)' },
  emptyText: { color: 'var(--board-text-muted, rgba(255,255,255,0.58))', textAlign: 'center', padding: '20px 0' },
  formRow: { display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', width: '100%', minWidth: 0 },
  activeToggle: { display: 'inline-flex', alignItems: 'center', gap: '8px', color: 'var(--board-text-soft, rgba(255,255,255,0.82))', fontSize: '13px', fontWeight: 700 },
  timelineHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '14px' },
  dateInput: { ...theme.input, colorScheme: 'var(--board-color-scheme, dark)' },
  timelineWrap: { ...theme.timelineSurface, overflow: 'auto', width: '100%', maxWidth: '100%', minWidth: 0, overscrollBehaviorX: 'contain' },
  timeline: { position: 'relative', minWidth: VEHICLE_LABEL_WIDTH + TIMELINE_WIDTH },
  hourHeader: { display: 'flex', borderBottom: '1px solid var(--board-border, rgba(255,255,255,0.12))', position: 'sticky', top: 0, background: 'var(--board-card-bg, rgba(10, 10, 25, 0.88))', zIndex: 10, backdropFilter: 'blur(16px)' },
  hourHeaderSpacer: { width: VEHICLE_LABEL_WIDTH, boxSizing: 'border-box', flexShrink: 0, borderRight: '1px solid var(--board-border, rgba(255,255,255,0.12))' },
  hourCell: { width: HOUR_WIDTH, boxSizing: 'border-box', flexShrink: 0, textAlign: 'center', padding: '9px 0', fontSize: '12px', color: 'var(--board-text-muted, rgba(255,255,255,0.58))', borderRight: '1px solid var(--board-border, rgba(255,255,255,0.07))' },
  vehicleRow: { display: 'flex', borderBottom: '1px solid var(--board-border, rgba(255,255,255,0.08))', position: 'relative', minHeight: ROW_HEIGHT },
  vehicleLabel: { width: VEHICLE_LABEL_WIDTH, boxSizing: 'border-box', flexShrink: 0, padding: '10px 14px', borderRight: '1px solid var(--board-border, rgba(255,255,255,0.12))', display: 'flex', flexDirection: 'column', justifyContent: 'center', background: 'var(--board-surface-soft, rgba(255,255,255,0.05))' },
  vehiclePlate: { fontWeight: 800, fontSize: '13px', color: 'var(--board-text-strong, #fff)' },
  vehicleType: { fontSize: '12px', color: 'var(--board-text-muted, rgba(255,255,255,0.58))', marginTop: '3px' },
  vehicleTrack: { position: 'relative', width: TIMELINE_WIDTH, flex: '0 0 ' + TIMELINE_WIDTH + 'px', background: 'repeating-linear-gradient(to right, transparent 0, transparent ' + (HOUR_WIDTH - 1) + 'px, color-mix(in srgb, var(--board-border, rgba(255,255,255,0.08)) 70%, transparent) ' + (HOUR_WIDTH - 1) + 'px, color-mix(in srgb, var(--board-border, rgba(255,255,255,0.08)) 70%, transparent) ' + HOUR_WIDTH + 'px)' },
  slot: { position: 'absolute', borderRadius: '6px', color: 'white', padding: '5px 8px', fontSize: '12px', overflow: 'hidden', userSelect: 'none', boxShadow: '0 12px 28px rgba(15,23,42,0.18)', boxSizing: 'border-box', border: '1px solid rgba(255,255,255,0.20)' },
  clickableSlot: { cursor: 'pointer' },
  slotTime: { fontWeight: 800, lineHeight: '16px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  slotInfo: { lineHeight: '15px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', opacity: 0.95 },
  modal: { position: 'fixed', inset: 0, backgroundColor: 'var(--board-overlay, rgba(0,0,0,0.62))', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 },
  modalContent: { ...theme.glassCard, color: 'var(--board-text-strong, #fff)', padding: '24px', width: '90%', maxWidth: '460px', boxSizing: 'border-box' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  closeBtn: { ...theme.button, ...theme.mutedButton, width: '34px', height: '34px', padding: 0, display: 'grid', placeItems: 'center' },
  detailRow: { display: 'flex', justifyContent: 'space-between', gap: '16px', padding: '10px 0', borderBottom: '1px solid var(--board-border, rgba(255,255,255,0.10))', fontSize: '14px', color: 'var(--board-text-soft, rgba(255,255,255,0.82))' },
  errorText: { color: 'var(--board-danger-text, #fecdd3)' },
  loadingText: { color: 'var(--board-text-muted, rgba(255,255,255,0.58))' },
};

function Vehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [vehicleClasses, setVehicleClasses] = useState([]);
  const [slots, setSlots] = useState([]);
  const [date, setDate] = useState(getLocalDateInputValue());
  const [licensePlate, setLicensePlate] = useState('');
  const [vehicleClassId, setVehicleClassId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const [isPanning, setIsPanning] = useState(false);
  const timelineScrollRef = useRef(null);
  const panRef = useRef(null);

  const loadVehicles = () => {
    setLoading(true);
    apiFetch(`${API_BASE}/vehicles`)
      .then(res => res.json())
      .then(data => { setVehicles(data); setLoading(false); })
      .catch(() => { setError('Fahrzeuge konnten nicht geladen werden.'); setLoading(false); });
  };

  const loadVehicleClasses = () => {
    apiFetch(`${API_BASE}/vehicle-classes?includeInactive=true`)
      .then(res => res.json())
      .then(data => setVehicleClasses(Array.isArray(data) ? data : []))
      .catch(() => setVehicleClasses([]));
  };

  const loadSlots = useCallback(() => {
    apiFetch(`${API_BASE}/slots?date=${date}`)
      .then(res => res.json())
      .then(data => setSlots(Array.isArray(data) ? data : []))
      .catch(() => setSlots([]));
  }, [date]);

  useEffect(() => {
    loadVehicles();
    loadVehicleClasses();
  }, []);
  useEffect(() => { loadSlots(); }, [loadSlots]);
  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const addVehicle = async (e) => {
    e.preventDefault();
    if (!licensePlate.trim()) return;
    await apiFetch(`${API_BASE}/vehicles`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        license_plate: licensePlate.trim(),
        vehicle_class_id: vehicleClassId ? Number(vehicleClassId) : null,
      })
    });
    setLicensePlate('');
    setVehicleClassId('');
    loadVehicles();
  };

  const deleteVehicle = async (id) => {
    if (!await showAppConfirm('Fahrzeug wirklich löschen?', {
      title: 'Fahrzeug löschen',
      confirmLabel: 'Löschen',
      danger: true,
    })) return;
    await apiFetch(`${API_BASE}/vehicles/${id}`, { method: 'DELETE' });
    loadVehicles();
  };

  const startEdit = (v) => {
    setEditingId(v.id);
    setEditData({
      license_plate: v.license_plate,
      vehicle_class_id: v.vehicle_class_id || '',
      is_active: v.is_active !== false,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const saveEdit = async (id) => {
    await apiFetch(`${API_BASE}/vehicles/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editData)
    });
    setEditingId(null);
    setEditData({});
    loadVehicles();
  };

  const handleTimelineWheel = useCallback((event) => {
    const element = timelineScrollRef.current;
    if (!element) return;
    const { deltaX, deltaY } = event;
    if (Math.abs(deltaY) <= Math.abs(deltaX)) return;
    event.preventDefault();
    element.scrollLeft += deltaY;
  }, []);

  const handleTimelinePanStart = useCallback((event) => {
    if (event.button !== 0) return;
    if (!event.target.closest?.('[data-pan-surface="true"]')) return;
    if (event.target.closest?.('[data-slot-id], button, input, select, textarea, label, a')) return;

    const element = timelineScrollRef.current;
    if (!element) return;

    event.preventDefault();
    panRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      scrollLeft: element.scrollLeft,
      scrollTop: element.scrollTop,
      windowScrollY: window.scrollY,
    };
    setIsPanning(true);
  }, []);

  useEffect(() => {
    if (!isPanning) return undefined;

    const onMove = event => {
      const pan = panRef.current;
      const element = timelineScrollRef.current;
      if (!pan || !element) return;

      const deltaX = event.clientX - pan.startX;
      const deltaY = event.clientY - pan.startY;
      const maxTop = element.scrollHeight - element.clientHeight;

      element.scrollLeft = pan.scrollLeft - deltaX;
      if (maxTop > 0) {
        element.scrollTop = pan.scrollTop - deltaY;
      } else {
        window.scrollTo({ left: window.scrollX, top: pan.windowScrollY - deltaY, behavior: 'auto' });
      }
    };

    const onUp = () => {
      panRef.current = null;
      setIsPanning(false);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isPanning]);

  const isMobile = viewportWidth < 760;
  const timelinePanCursor = isMobile ? 'default' : isPanning ? 'grabbing' : 'grab';

  const renderTimeline = () => (
    <div style={styles.card}>
      <div style={{ ...styles.timelineHeader, flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center' }}>
        <h2 style={{ margin: 0 }}>Fahrzeug-Timeline</h2>
        <input
          type="date"
          style={{ ...styles.dateInput, width: isMobile ? '100%' : undefined }}
          value={date}
          onChange={e => setDate(e.target.value)}
        />
      </div>

      <div
        ref={timelineScrollRef}
        style={{ ...styles.timelineWrap, cursor: timelinePanCursor }}
        onWheel={handleTimelineWheel}
        onMouseDown={handleTimelinePanStart}
      >
        <div style={styles.timeline}>
          <div style={{ ...styles.hourHeader, cursor: timelinePanCursor }} data-pan-surface="true">
            <div style={{ ...styles.hourHeaderSpacer, cursor: timelinePanCursor }} data-pan-surface="true"></div>
            {Array.from({ length: 24 }).map((_, h) => (
              <div key={h} style={{ ...styles.hourCell, cursor: timelinePanCursor }} data-pan-surface="true">
                {String(h).padStart(2, '0')}:00
              </div>
            ))}
          </div>

          {vehicles.map(vehicle => {
            const vehicleSlots = slots.filter(slot => slot.vehicle_id === vehicle.id);
            const { items: laidOutSlots, laneCount } = layoutVehicleSlots(vehicleSlots, date);
            const rowHeight = laneCount * ROW_HEIGHT;

            return (
              <div key={vehicle.id} style={{ ...styles.vehicleRow, minHeight: `${rowHeight}px` }}>
                <div style={{ ...styles.vehicleLabel, cursor: timelinePanCursor }} data-pan-surface="true">
                  <span style={styles.vehiclePlate}>{vehicle.license_plate}</span>
                  {vehicle.vehicle_class_name && <span style={styles.vehicleType}>{vehicle.vehicle_class_name}</span>}
                </div>
                <div style={{ ...styles.vehicleTrack, height: `${rowHeight}px`, cursor: timelinePanCursor }} data-pan-surface="true">
                  {laidOutSlots.map(({ slot, startMin, endMin, lane }) => {
                    const left = (startMin / 60) * HOUR_WIDTH;
                    const width = ((endMin - startMin) / 60) * HOUR_WIDTH;
                    const timeLabel = `${minutesToHHMM(startMin)} - ${minutesToHHMM(endMin)}`;
                    const jobLabel = getSlotJobs(slot).join(', ');
                    const gateName = slot.gate_name || '';
                    const titleParts = [timeLabel, gateName, jobLabel].filter(Boolean);

                    return (
                      <div
                        key={slot.id}
                        data-slot-id={slot.id}
                        style={{
                          ...styles.slot,
                          ...styles.clickableSlot,
                          left: `${left}px`,
                          top: `${lane * ROW_HEIGHT + 6}px`,
                          width: `${width}px`,
                          height: `${ROW_HEIGHT - 12}px`,
                          backgroundColor: DIRECTION_COLORS[slot.direction] || '#777',
                        }}
                        title={titleParts.join(' | ')}
                        onClick={() => setSelectedSlot(slot)}
                      >
                        <div style={styles.slotTime}>{timeLabel}</div>
                        {gateName && <div style={styles.slotInfo}>{gateName}</div>}
                        {jobLabel && <div style={styles.slotInfo}>{jobLabel}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <div style={styles.stack}>
      <div style={styles.card}>
        <h2 style={{ marginTop: 0 }}>Neues Fahrzeug anlegen</h2>
        <form onSubmit={addVehicle}>
          <div style={{ ...styles.formRow, flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center' }}>
            <input
              style={{ ...styles.input, width: '100%', marginRight: 0 }}
              placeholder="Kennzeichen * (z.B. B-AB 1234)"
              value={licensePlate}
              onChange={e => setLicensePlate(e.target.value)}
              required
            />
            <select
              style={{ ...styles.select, width: isMobile ? '100%' : styles.select.minWidth, minWidth: isMobile ? 0 : styles.select.minWidth, marginRight: 0 }}
              value={vehicleClassId}
              onChange={e => setVehicleClassId(e.target.value)}
            >
              <option style={styles.option} value="">- Fahrzeugklasse -</option>
              {vehicleClasses.map(vehicleClass => (
                <option style={styles.option} key={vehicleClass.id} value={vehicleClass.id}>
                  {vehicleClass.name}
                </option>
              ))}
            </select>
            <button style={{ ...styles.button, width: isMobile ? '100%' : 'auto' }} type="submit">Hinzufügen</button>
          </div>
        </form>
      </div>

      <div style={styles.card}>
        <h2 style={{ marginTop: 0 }}>Fahrzeuge ({vehicles.length})</h2>

        {error && <p style={styles.errorText}>{error}</p>}
        {loading && <p style={styles.loadingText}>Lade Fahrzeuge...</p>}

        {!loading && !error && (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>ID</th>
                  <th style={styles.th}>Kennzeichen</th>
                  <th style={styles.th}>Klasse</th>
                  <th style={styles.th}>Aktiv</th>
                  <th style={styles.th}>Erstellt am</th>
                  <th style={styles.th}>Aktion</th>
                </tr>
              </thead>
              <tbody>
                {vehicles.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ ...styles.td, ...styles.emptyText }}>
                      Keine Fahrzeuge vorhanden
                    </td>
                  </tr>
                ) : (
                  vehicles.map(v => (
                    <tr key={v.id} style={v.is_active === false ? { opacity: 0.62 } : undefined}>
                      <td style={styles.td}>{v.id}</td>

                      {editingId === v.id ? (
                        <>
                          <td style={styles.td}>
                            <input
                              style={styles.inlineInput}
                              value={editData.license_plate}
                              onChange={e => setEditData({ ...editData, license_plate: e.target.value })}
                            />
                          </td>
                        <td style={styles.td}>
                          <select
                            style={styles.inlineSelect}
                            value={editData.vehicle_class_id || ''}
                            onChange={e => setEditData({ ...editData, vehicle_class_id: e.target.value })}
                          >
                            <option style={styles.option} value="">- keine -</option>
                            {vehicleClasses.map(vehicleClass => (
                              <option style={styles.option} key={vehicleClass.id} value={vehicleClass.id}>
                                {vehicleClass.name}
                              </option>
                            ))}
                          </select>
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={styles.td}><strong>{v.license_plate}</strong></td>
                        <td style={styles.td}>{v.vehicle_class_name || '-'}</td>
                      </>
                    )}

                      <td style={styles.td}>
                        {editingId === v.id ? (
                          <label style={styles.activeToggle}>
                            <input
                              type="checkbox"
                              checked={editData.is_active !== false}
                              onChange={e => setEditData({ ...editData, is_active: e.target.checked })}
                            />
                            Aktiv
                          </label>
                        ) : (
                          <span style={v.is_active === false ? styles.inactiveBadge : styles.badge}>
                            {v.is_active === false ? 'Inaktiv' : 'Aktiv'}
                          </span>
                        )}
                      </td>
                      <td style={styles.td}>{new Date(v.created_at).toLocaleDateString('de-DE')}</td>
                      <td style={styles.td}>
                        {editingId === v.id ? (
                          <>
                            <button style={styles.saveBtn} onClick={() => saveEdit(v.id)}>Speichern</button>
                            <button style={styles.cancelBtn} onClick={cancelEdit}>Abbrechen</button>
                          </>
                        ) : (
                          <>
                            <button style={styles.editBtn} onClick={() => startEdit(v)}>Bearbeiten</button>
                            <button style={styles.deleteBtn} onClick={() => deleteVehicle(v.id)}>Löschen</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {renderTimeline()}

      {selectedSlot && (
        <AssignmentModal
          slot={selectedSlot}
          vehicle={vehicles.find(v => v.id === selectedSlot.vehicle_id)}
          onClose={() => setSelectedSlot(null)}
        />
      )}
    </div>
  );
}

function AssignmentModal({ slot, vehicle, onClose }) {
  const jobs = getSlotJobs(slot);

  return (
    <div style={styles.modal} onClick={onClose}>
      <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={{ margin: 0 }}>Zuweisung</h3>
          <button style={styles.closeBtn} onClick={onClose}>x</button>
        </div>

        <div style={styles.detailRow}>
          <strong>Datum</strong>
          <span>{unixToDate(slot.start_time)}</span>
        </div>
        <div style={styles.detailRow}>
          <strong>Uhrzeit</strong>
          <span>{unixToTime(slot.start_time)} - {unixToTime(slot.end_time)}</span>
        </div>
        <div style={styles.detailRow}>
          <strong>Job</strong>
          <span>{jobs.length > 0 ? jobs.join(', ') : '-'}</span>
        </div>
        {slot.driver_name && (
          <div style={styles.detailRow}>
            <strong>Fahrer</strong>
            <span>{slot.driver_name}</span>
          </div>
        )}
        <div style={styles.detailRow}>
          <strong>Richtung</strong>
          <span>{DIRECTION_LABELS[slot.direction] || slot.direction || '-'}</span>
        </div>
        {slot.gate_name && (
          <div style={styles.detailRow}>
            <strong>Tor</strong>
            <span>{slot.gate_name}</span>
          </div>
        )}
        {(vehicle?.license_plate || slot.license_plate) && (
          <div style={styles.detailRow}>
            <strong>Fahrzeug</strong>
            <span>{vehicle?.license_plate || slot.license_plate}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default Vehicles;
